import { z } from "zod";

const SUCCESS = z.object({ accountId: z.string().min(1), sessionToken: z.string().min(1) });
const ERROR = z.object({ error: z.string() });
export type AuthAction = "signup" | "login" | "upgrade";
export type AuthFailure =
  | "network"
  | "badResponse"
  | "emailTaken"
  | "invalidCredentials"
  | "invalidSession"
  | "badRequest"
  | "internal";
export type AuthResult =
  | { readonly ok: true; readonly sessionToken: string | null }
  | { readonly ok: false; readonly error: AuthFailure };

function failure(raw: unknown): AuthFailure {
  const result = ERROR.safeParse(raw);
  const code = result.success ? result.data.error : "badResponse";
  return code === "emailTaken" ||
    code === "invalidCredentials" ||
    code === "invalidSession" ||
    code === "badRequest" ||
    code === "internal"
    ? code
    : "badResponse";
}

export async function submitAuth(
  action: AuthAction,
  email: string,
  password: string,
  sessionToken: string,
): Promise<AuthResult> {
  const body = action === "upgrade" ? { sessionToken, email, password } : { email, password };
  try {
    const response = await fetch("/api/auth/" + action, {
      method: "POST",
      headers: { "content-type": "application/json" },
      body: JSON.stringify(body),
    });
    const raw: unknown = await response.json().catch(() => null);
    if (!response.ok) return { ok: false, error: failure(raw) };
    if (action === "upgrade") return { ok: true, sessionToken: null };
    const parsed = SUCCESS.safeParse(raw);
    return parsed.success
      ? { ok: true, sessionToken: parsed.data.sessionToken }
      : { ok: false, error: "badResponse" };
  } catch {
    return { ok: false, error: "network" };
  }
}

export async function logout(sessionToken: string): Promise<void> {
  try {
    await fetch("/api/auth/logout", {
      method: "POST",
      headers: { "content-type": "application/json" },
      body: JSON.stringify({ sessionToken }),
    });
  } catch {
    /* local logout remains useful offline */
  }
}
